import { useDataProvider, useNotify } from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

import type { CostSettings } from "../money/profit";
import { Field, SimpleSelect } from "../SimpleSelect";
import { agentName, type Agent } from "../useAgents";
import { parseAmount } from "./commissions";
import { addCommission } from "./useCommissions";

/** A hand-made line: a bonus, a correction, a deduction (negative amount). */
export const AdjustmentDialog = ({
  open,
  onOpenChange,
  agents,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agents: Agent[];
  onSaved: () => void;
}) => {
  const notify = useNotify();
  const [salesId, setSalesId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setSalesId("");
      setAmount("");
      setNote("");
    }
  }, [open]);
  const value = parseAmount(amount);
  const ready = !!salesId && value != null && note.trim().length > 0;

  const save = async () => {
    if (!ready || value == null) return;
    setSaving(true);
    try {
      await addCommission(salesId, value, note.trim());
      notify("Ajuste añadido", { type: "success" });
      onSaved();
      onOpenChange(false);
    } catch (error) {
      notify(error instanceof Error ? error.message : "No se ha podido", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Añadir un ajuste</DialogTitle>
          <DialogDescription>
            Un extra, una corrección o un descuento (importe en negativo). Queda
            en el libro como pendiente.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <SimpleSelect
            label="Para"
            value={salesId}
            onChange={setSalesId}
            options={agents
              .filter((agent) => !agent.no_work)
              .map((agent) => ({
                value: String(agent.id),
                label: agentName(agent),
              }))}
          />
          <Field label="Importe (€)">
            <Input
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="20 o -5"
            />
          </Field>
          <Field label="Motivo">
            <Input
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Bono del mes, venta cerrada por WhatsApp, devolución…"
            />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button disabled={!ready || saving} onClick={save}>
            Añadir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** The Stripe payment links the setters send when a client wants the plan. */
export const MaintenanceLinksDialog = ({
  open,
  onOpenChange,
  settings,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  settings: CostSettings | undefined;
  onSaved: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [monthly, setMonthly] = useState("");
  const [yearly, setYearly] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setMonthly(settings?.maintenance_monthly_link ?? "");
      setYearly(settings?.maintenance_yearly_link ?? "");
    }
  }, [open, settings]);

  const save = async () => {
    setSaving(true);
    try {
      await dataProvider.update("gula_cost_settings", {
        id: 1,
        data: {
          maintenance_monthly_link: monthly.trim() || null,
          maintenance_yearly_link: yearly.trim() || null,
        },
        previousData: { id: 1 },
      });
      notify("Enlaces guardados", { type: "success" });
      onSaved();
      onOpenChange(false);
    } catch (error) {
      notify(error instanceof Error ? error.message : "No se ha podido", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Enlaces de pago del mantenimiento</DialogTitle>
          <DialogDescription>
            Los enlaces de Stripe que los setters mandan por WhatsApp en la
            llamada de bienvenida. Cuando el cliente paga, la suscripción llega
            sola al CRM y se apunta el cierre.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Field label="Plan mensual (9,99 €/mes)">
            <Input
              value={monthly}
              onChange={(event) => setMonthly(event.target.value)}
              placeholder="https://buy.stripe.com/…"
            />
          </Field>
          <Field label="Plan anual">
            <Input
              value={yearly}
              onChange={(event) => setYearly(event.target.value)}
              placeholder="https://buy.stripe.com/…"
            />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button disabled={saving} onClick={save}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
